import React, { useEffect, useState, useMemo } from "react";
import { Container, Card, Table, Button, Badge, Spinner, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import InformacionUsuario from "../modales/InformacionUsuario";
import AvatarCentrado from "../modales/AvatarCentrado";

import {API_BASE} from "../../services/apirest";
import { invalidateSolicitudesCache } from "../../services/solicitudesCache";

export default function SupervisorModificaciones() {
  const [perfil, setPerfil] = useState(null);
  const [perfilLoading, setPerfilLoading] = useState(true);
  const [list, setList] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false); 
  const [selected, setSelected] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false); 
  const [rejectLoading, setRejectLoading] = useState(false);                  
  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
    []
  );

  const roleLabels = useMemo(() => ({
    8: "VENDEDOR (ACTIVE DIRECTORI/SIBEL TELCOM)",
    9: "VENDEDOR / INSTALADOR (TOA)",
    10: "SUPERVISOR",
    11: "ADMINISTRATIVO",
  }), []);

  useEffect(() => {
    setPerfilLoading(true);
    fetch(`${API_BASE}/perfil.php`, { headers })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") setPerfil(d.result.user);
      })
      .catch(() => setPerfil(null))
      .finally(() => setPerfilLoading(false));
  }, [headers]);

  const confirmApprove = async () => {
    if (!selected) return;
    setApproveLoading(true);
    const ok = await handleAprobar(selected.SolicitudId);
    setApproveLoading(false);
    if (ok) {
      setShowApproveModal(false);
      setSelected(null);
    }
  };

  const ultimosRoles = [
    { id: 8, nombre:  "VENDEDOR (ACTIVE DIRECTORY/SIEBEL TELCOM)" },
    { id: 9, nombre:  "VENDEDOR / INSTALADOR (TOA)" },
    { id: 10, nombre: "SUPERVISOR" },
    { id: 11, nombre: "ADMINISTRATIVO" },
  ];

  const modData = useMemo(() => {
    try {
      return JSON.parse(selected?.modificaciones_data || "{}");
    } catch {
      return {};
    }
  }, [selected?.modificaciones_data]);

  const getRolNombre = (id) =>
    (roleLabels?.[Number(id)] ?? `Rol ${id}`).toUpperCase();

  useEffect(() => {
    if (perfilLoading) return;
    fetch(`${API_BASE}/solicitudes.php?modificacion=pendientes`, { headers })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") setList(d.result.solicitudes);
        else setList([]);
      })
      .catch(() => setList([]));
  }, [headers, perfilLoading]);

  const filteredList = useMemo(() => {
    if (!Array.isArray(list) || perfilLoading) return [];
    const rol = Number(perfil?.rol_id);
    const usuarioId = perfil?.UsuarioId ?? perfil?.usuario_id ?? perfil?.id;
    if (rol === 3) {
      if (usuarioId == null) return [];
      return list.filter(
        (s) =>
          String(s?.SupervisorModificaciones ?? s?.supervisor_usuario_id ?? s?.supervisor_id ?? "") ===
          String(usuarioId)
      );
    }
    return list;
  }, [list, perfil, perfilLoading]);

  const triggerGlobalRefresh = React.useCallback(() => {
    invalidateSolicitudesCache(key => key.includes('/solicitudes.php'));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('solicitudes:refresh'));
    }
  }, []);

  const handleAprobar = async (SolicitudId) => {
    try {
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          action: "aprobar_modificacion",
          SolicitudId,
        }),
      });
      const j = await res.json();
      if (j.status === "success") {
        setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));
        Swal.fire("Listo", "Modificación aprobada.", "success");
        window.dispatchEvent(new Event("solicitudes:refresh"));
        window.dispatchEvent(new Event("inbox:refresh"));
        triggerGlobalRefresh();
        return true;
      } else {
        Swal.fire("Error", j.result?.error_msg || "No se pudo aprobar", "error");
        return false;
      }
    } catch (e) {
      Swal.fire("Error", "Fallo de red o servidor.", "error");
      return false;
    }
  };

  const handleRechazar = async (SolicitudId) => {
    try {
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          action: "rechazar_modificacion",
          SolicitudId,
        }),
      });
      const j = await res.json();
      if (j.status === "success" || j.status === "ok") {
        setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));
        Swal.fire("Listo", "Modificación rechazada.", "success");
        window.dispatchEvent(new Event("solicitudes:refresh"));
        window.dispatchEvent(new Event("inbox:refresh"));
        triggerGlobalRefresh();
        return true;
      } else {
        Swal.fire("Error", j.result?.error_msg || "No se pudo rechazar", "error");
        return false;
      }
    } catch (e) {
      Swal.fire("Error", "Fallo de red o servidor.", "error");
      return false;
    }
  };

  if (list === null || perfilLoading) {
    return (
      <Container className="d-flex justify-content-center" style={{ height: "60vh" }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <div className="app-container">
      <Container className="my-5">
        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Aprobación de Modificaciones</h5>
            <Badge bg={filteredList.length > 0 ? "info" : "secondary"} pill>
              {filteredList.length}
            </Badge>
          </Card.Header>
          <Card.Body>
            {filteredList.length === 0 ? (
              <p className="text-center text-muted">
                No hay modificaciones pendientes.
              </p>
            ) : (
              <Table responsive hover striped className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Nombre Empleado</th>
                    <th>Creado Por</th>
                    <th>Correo</th>
                    <th>RFC</th>
                    <th>No. Empleado</th>
                    <th>Detalles Modif.</th>
                    <th className="text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((s) => (
                    <tr key={s.SolicitudId}>
                      <td>{s?.nombre_completo || [s?.nombre_usuario, s?.apellido_paterno, s?.apellido_materno].filter(Boolean).join(' ')}</td>
                      <td>{`${s.creado_por_nombre} ${s.creado_por_apellido}`}</td>
                      <td>{s.correo}</td>
                      <td>{s.rfc}</td>
                      <td>{s.numero_vendedor}</td>
                      <td>
                        {(() => {
                          let data = {};
                          try {
                            data = JSON.parse(s.modificaciones_data || "{}");
                          } catch {}
                          return `Nuevo rol: ${getRolNombre(data.new_rol_id)}`;
                        })()}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => {
                            setSelected(s);
                            setShowApproveModal(true);
                          }}
                        >
                          Aprobar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>
     <Modal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        centered
        size="lg"
        backdrop="static"
        style={{ zIndex: 11000 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Aprobar modificación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
              <>
            
                <AvatarCentrado src={selected?.foto_url} alt="Foto del vendedor"/>
                {/* 1) Distribuidor */}
                <InformacionUsuario
                    user={selected}
                    title="Distribuidor"
                    cols={2}
                    fields={[
                      { key: "distribuidor_nombre", label: "Distribuidor" },
                      {
                        key: "distribuidor_rs",
                        label: "Razón Social",
                        render: (u) => u.distribuidor_rs || "—",
                      },
                    ]}
                />
                <InformacionUsuario
                    user={selected}
                    title="Datos del usuario"
                    cols={3}
                    fields={[
                      { key: "usuario",          label: "Email" }, // ojo: es selected.usuario
                      {
                        key: "nombre",
                        label: "Nombre",
                        render: (u) => [u?.nombre_usuario, u?.apellido_paterno, u?.apellido_materno]
                            .filter(Boolean)
                            .join(" "),
                      },
                      { key: "rfc",              label: "RFC", render: (u) => (u?.rfc || "").toUpperCase() },
                      { key: "telefono",         label: "Teléfono" },
                      { key: "Fecha",            label: "Fecha de Solicitud", render: (u) => new Date(u?.Fecha).toLocaleDateString("es-MX") },
                      { key: "numero_vendedor",  label: "Número Empleado" },
                    ]}
                />
                <InformacionUsuario
                    user={selected}
                    title="Puesto Actual"
                    cols={2}
                    fields={[
                      {
                        key: "rol_id",
                        label: "Puesto actual",
                        render: (u) => {
                          const id = Number(u?.rol_id);
                          return ultimosRoles.find(r => r.id === id)?.nombre ?? "-";
                        }
                      },
                    ]}
                />

                 {/* 5) Bodega (SOLO si hay datos) */}
                    {(
                      selected?.bodega_distribuidor ||
                      selected?.bodega_almacen ||
                      selected?.bodega_nombre ||
                      selected?.bodega_region ||
                      selected?.bodega_direccion
                    ) && (
                      <InformacionUsuario
                        user={selected}
                        title="Bodega"
                        cols={2}
                        hideEmpty={true}
                        fields={[
                          { key: "bodega_distribuidor", label: "Distribuidor" },
                          { key: "bodega_almacen",      label: "Almacén" },
                          { key: "bodega_nombre",       label: "Bodega" },
                          { key: "bodega_region",       label: "Región" },
                          { key: "bodega_direccion",    label: "Dirección" },
                        ]}
                      />
                    )}
                <InformacionUsuario
                    user={selected}
                    title="Cambios propuestos"
                    cols={1}
                    fields={[
                      {
                        key: "new_rol_id",
                        label: "Nuevo rol",
                        render: () =>
                            modData?.new_rol_id ? getRolNombre(modData.new_rol_id) : "—",
                      },
                    ]}
                />
              </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowApproveModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            disabled={approveLoading || !selected}
            onClick={confirmApprove}
          >
            {approveLoading ? "Procesando..." : "Aprobar"}
          </Button>
            <Button
              variant="danger"
              disabled={rejectLoading || !selected}
              onClick={async () => {
                setRejectLoading(true);
                const ok = await handleRechazar(selected.SolicitudId);
                setRejectLoading(false);
                if (ok) {
                  setShowApproveModal(false);
                  setSelected(null);
                }
              }}
            >
              {rejectLoading ? "Procesando..." : "Rechazar"}
            </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
